import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useNavigate, useLocation } from 'react-router-dom';
import ItemResolver from "../items/ItemResolver.js";
import CustomDivider from "../dsasComponents/CustomDivider.js";

const toggleTagInSearch = ({ location, navigate, tagId }) => {
  const searchParams = new URLSearchParams(location.search);
  const currentTags = searchParams.get('tags')
    ? searchParams.get('tags').split(',')
    : [];

  if (currentTags.includes(tagId)) {
    const updatedTags = currentTags.filter((tag) => tag !== tagId);
    if (updatedTags.length > 0) {
      searchParams.set('tags', updatedTags.join(','));
    } else {
      searchParams.delete('tags');
    }
  } else {
    currentTags.push(tagId);
    searchParams.set('tags', currentTags.join(','));
  }

  navigate(
    {
      pathname: location.pathname,
      search: buildSearchString(searchParams),
    },
    { replace: false }
  );
};

const buildSearchString = (searchParams) => {
  const nextSearch = searchParams.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

const getItemOrderDate = (item) => item?.displayDate || item?.creation;

const getItemTimestamp = (item) => {
  const rawDate = getItemOrderDate(item);
  const parsed = rawDate ? new Date(rawDate) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : 0;
};

const getItemDayKey = (item) => {
  const rawDate = getItemOrderDate(item);
  if (!rawDate) return null;

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed.toISOString().slice(0, 10);
};

const dedupeItems = (items = []) => {
  const map = new Map();

  items.forEach((item) => {
    if (!item?._id) return;
    const key = `${item.type || 'unknown'}-${item._id}`;
    if (!map.has(key)) {
      map.set(key, item);
    }
  });

  return Array.from(map.values());
};

const sortThings = (a, b) => getItemTimestamp(b) - getItemTimestamp(a);

const getDisplayDayDate = (day) => {
  if (!day) return null;
  const parsed = new Date(`${day}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDayLabel = (day) => {
  const parsed = getDisplayDayDate(day);

  if (!parsed) {
    return 'Date inconnue';
  }

  const today = new Date();
  const todayKey = today.toISOString().slice(0, 10);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = yesterday.toISOString().slice(0, 10);

  if (day === todayKey) {
    return "Aujourd'hui";
  }

  if (day === yesterdayKey) {
    return 'Hier';
  }

  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  }).format(parsed);
};

const buildCombinedItems = (actu) => {
  const buildQuestionBoxes = actu?.questionBoxes ? actu.questionBoxes.map((item) => ({ ...item, type: 'questionBox' })) : [];
  const buildQuizzs = actu?.quizzs ? actu.quizzs.map((item) => ({ ...item, type: 'quizz' })) : [];
  const buildSurveys = actu?.surveys ? actu.surveys.map((item) => ({ ...item, type: 'survey' })) : [];
  const buildNotations = actu?.notations ? actu.notations.map((item) => ({ ...item, type: 'notation' })) : [];
  const buildArticles = actu?.articles ? actu.articles.map((item) => ({ ...item, type: 'article' })) : [];

  return buildQuizzs
    .concat(buildQuestionBoxes)
    .concat(buildSurveys)
    .concat(buildNotations)
    .concat(buildArticles)
    .sort(sortThings);
};

const ActuView = ({
  actu,
  loadMore,
  onSelectDay,
  onGoToPreviousDay,
  onGoToNextDay,
}) => {
    console.log("Rendering ActuView with actu:", actu);
  const [pinItems, setPinItems] = useState([]);
  const [items, setItems] = useState([]);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const redirectUrlFromUrl = queryParams.get('redirect-url');
  const displayTextFromUrl = queryParams.get('display-text');
  const buttonPositionFromUrl = queryParams.get('button-position') || 'top';

  const availableDays = actu?.availableDays || actu?.dayFeed?.availableDays || [];
  const selectedDay = actu?.selectedDay || actu?.dayFeed?.selectedDay;
  const selectedDayCount = actu?.selectedDayCount || actu?.dayFeed?.selectedDayCount || 0;
  const showDayNavigation = availableDays.length > 1;

  const navigate = useNavigate();

  const handleInlineTagClick = (tagId) => {
    toggleTagInSearch({ location, navigate, tagId });
  };

useEffect(() => {
  const combinedItems = dedupeItems(buildCombinedItems(actu || {}));

  setPinItems(
    combinedItems
      .filter((item) => item.isPin)
      .sort(sortThings)
  );

  setItems(
    combinedItems
      .filter((item) => !item.isPin)
      .filter((item) => !selectedDay || getItemDayKey(item) === selectedDay)
      .sort(sortThings)
  );
}, [actu, selectedDay]);

  useEffect(() => {
    if (!selectedDay) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedDay]);

  const disabledShowMore = useMemo(() => !actu?.hasMore, [actu?.hasMore]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', ml: '1.2em', mb: '1em' }}>
        <Box
          sx={{
            float: 'left',
            color: 'white',
            width: 'fit-content',
            padding: '3px 9px 3px 7px',
            display: 'flex',
            background: '#CB1F1F 0% 0% no-repeat padding-box',
            borderRadius: '20px',
            mt: 1,
          }}
        >
          <Typography sx={{ fontSize: '0.7em', whiteSpace: 'nowrap' }}>&bull; DIRECT</Typography>
        </Box>
        <Typography
          gutterBottom
          component="div"
          variant="h1"
          sx={{
            mt: '.3em',
            mb: '.3em',
            ml: '.4em',
            textTransform: 'none',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {actu?.name}
        </Typography>
      </Box>

      <TagsDisplay actu={actu}  onTagClick={handleInlineTagClick} />

      {redirectUrlFromUrl && displayTextFromUrl && buttonPositionFromUrl === 'top' && (
        <CustomRedirectButton redirectUrl={redirectUrlFromUrl} displayText={displayTextFromUrl} />
      )}

      {showDayNavigation && (
        <DayNavigation
          availableDays={availableDays}
          selectedDay={selectedDay}
          selectedDayCount={selectedDayCount}
          onSelectDay={onSelectDay}
          onGoToPreviousDay={onGoToPreviousDay}
          onGoToNextDay={onGoToNextDay}
        />
      )}

      {pinItems.length > 0 && (
        <Stack spacing={0}>
          {pinItems.map((item) => (
            <React.Fragment key={item._id}>
              <ItemResolver item={item} match={actu} onTagClick={handleInlineTagClick}  />
            </React.Fragment>
          ))}
          <CustomDivider />
        </Stack>
      )}

      {items.length > 0 ? (
        <Stack spacing={0}>
          {items.map((item, idx) => (
            <React.Fragment key={item._id}>
              <ItemResolver item={item} match={actu} onTagClick={handleInlineTagClick}  />
              <CustomDivider disabled={idx === items.length - 1} />
            </React.Fragment>
          ))}
        </Stack>
      ) : (
        <Box sx={{ px: 2, py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Aucun contenu disponible pour cette journée.
          </Typography>
        </Box>
      )}

      {!disabledShowMore ? (
        <BottomButton label="Voir plus" onClick={loadMore} />
      ) : (
        <BottomButton label="Début de journée" />
      )}

      {showDayNavigation && (
        <DayNavigation
          availableDays={availableDays}
          selectedDay={selectedDay}
          selectedDayCount={selectedDayCount}
          onSelectDay={onSelectDay}
          onGoToPreviousDay={onGoToPreviousDay}
          onGoToNextDay={onGoToNextDay}
          compact
        />
      )}

      {redirectUrlFromUrl && displayTextFromUrl && buttonPositionFromUrl === 'bottom' && (
        <CustomRedirectButton redirectUrl={redirectUrlFromUrl} displayText={displayTextFromUrl} />
      )}
    </Box>
  );
};

const DayNavigation = ({
  availableDays,
  selectedDay,
  selectedDayCount,
  onSelectDay,
  onGoToPreviousDay,
  onGoToNextDay,
  compact = false,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const selectedIndex = availableDays.findIndex((day) => day.date === selectedDay);
    const isPreviousDisabled = selectedIndex === -1 || selectedIndex >= availableDays.length - 1;
    const isNextDisabled = selectedIndex <= 0;

  return (
    <Box
      sx={{
        px: compact ? 1 : 2,
        py: compact ? 1 : 1.5,
        mt: compact ? 2 : 0,
        mb: compact ? 1 : 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: '16px',
          px: compact ? 1 : 1.5,
          py: compact ? 0.8 : 1,
          backgroundColor: '#fff',
        }}
      >
        <Button
          variant="text"
          onClick={onGoToPreviousDay}
          disabled={isPreviousDisabled}
          sx={{ minWidth: compact ? 36 : 42, borderRadius: '999px' }}
        >
          ←
        </Button>

        <Button
          variant="text"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            flex: 1,
            textTransform: 'none',
            color: 'inherit',
            minHeight: compact ? 36 : 44,
            borderRadius: '12px',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 600, fontSize: compact ? '0.9rem' : '1rem' }}>
              {formatDayLabel(selectedDay)}
            </Typography>
            {!compact && typeof selectedDayCount === 'number' && (
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                {selectedDayCount} post{selectedDayCount > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        </Button>

        <Button
          variant="text"
          onClick={onGoToNextDay}
          disabled={isNextDisabled}
          sx={{ minWidth: compact ? 36 : 42, borderRadius: '999px' }}
        >
          →
        </Button>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {availableDays.map((day) => {
          const isSelected = day.date === selectedDay;

          return (
            <MenuItem
              key={day.date}
              selected={isSelected}
              onClick={() => {
                setAnchorEl(null);
                onSelectDay?.(day.date);
              }}
              sx={{ minWidth: 240, display: 'flex', justifyContent: 'space-between', gap: 2 }}
            >
              <Typography sx={{ fontWeight: isSelected ? 700 : 400 }}>
                {formatDayLabel(day.date)}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                {day.count}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

const BottomButton = ({ label, onClick }) => {
  const disabled = onClick === undefined;

  return (
    <Box sx={{ position: 'relative', width: '100%', pb: '1em', mt: 5 }}>
      <div style={{ position: 'absolute', bottom: '10px', width: '100%' }}>
        <CustomDivider />
      </div>

      <Button
        sx={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'fit-content',
          backgroundColor: disabled ? 'rgba(200,200,200,1) !important' : 'rgba(0,0,0,1) !important',
          color: disabled ? 'rgb(50,50,50) !important' : 'white !important',
          boxShadow: 'none',
          borderRadius: '20px',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        onClick={onClick ? onClick : () => {}}
        disabled={disabled}
        variant="contained"
      >
        {label}
      </Button>
    </Box>
  );
};

const CustomRedirectButton = ({ redirectUrl, displayText }) => {
  if (!redirectUrl) return null;

  let finalUrl = redirectUrl.trim();

  if (!/^https?:\/\//i.test(finalUrl)) {
    finalUrl = `https://${finalUrl}`;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 2 }}>
      <Button
        variant="contained"
        onClick={() => window.open(finalUrl, '_blank', 'noopener,noreferrer')}
        sx={{
          borderRadius: '20px',
          textTransform: 'none',
          px: 3,
          py: 0.8,
          fontWeight: 300,
        }}
      >
        {displayText}
      </Button>
    </Box>
  );
};

const TagsDisplay = ({ actu, onTagClick }) => {
  const navigate = useNavigate();
  const location = useLocation();

//   const handleTagClick = (tagId) => {
//     const searchParams = new URLSearchParams(location.search);
//     const currentTags = searchParams.get('tags') ? searchParams.get('tags').split(',') : [];

//     if (currentTags.includes(tagId)) {
//       const updatedTags = currentTags.filter((tag) => tag !== tagId);
//       if (updatedTags.length > 0) {
//         searchParams.set('tags', updatedTags.join(','));
//       } else {
//         searchParams.delete('tags');
//       }
//     } else {
//       currentTags.push(tagId);
//       searchParams.set('tags', currentTags.join(','));
//     }

//     navigate(
//       {
//         pathname: location.pathname,
//         search: buildSearchString(searchParams),
//       },
//       { replace: false }
//     );
//   };

  const selectedTags = new URLSearchParams(location.search).get('tags')?.split(',') || [];

  const mediaTags = [
    ...(actu?.quizzs ? actu.quizzs.flatMap((item) => item.tags || []) : []),
    ...(actu?.surveys ? actu.surveys.flatMap((item) => item.tags || []) : []),
    ...(actu?.notations ? actu.notations.flatMap((item) => item.tags || []) : []),
    ...(actu?.articles ? actu.articles.flatMap((item) => item.tags || []) : []),
  ];

  const allUsedTags = useMemo(() => {
    const usedTags = [...new Set([...(actu?.usedTags || []), ...mediaTags.map((tag) => tag.toString())])];
    return usedTags.filter((tagId) => actu?.tags?.find((tag) => tag._id === tagId));
  }, [actu, mediaTags]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentTags = searchParams.get('tags') ? searchParams.get('tags').split(',') : [];
    const validTags = currentTags.filter((tag) => allUsedTags.includes(tag));

    if (validTags.length === currentTags.length) {
      return;
    }

    if (validTags.length > 0) {
      searchParams.set('tags', validTags.join(','));
    } else {
      searchParams.delete('tags');
    }

    navigate(
      {
        pathname: location.pathname,
        search: buildSearchString(searchParams),
      },
      { replace: true }
    );
  }, [allUsedTags, location.pathname, location.search, navigate]);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      {allUsedTags.map((usedTagId) => {
        const tag = actu?.tags?.find((item) => item._id === usedTagId);
        const isSelected = tag && selectedTags.includes(tag._id);

        return tag ? (
          <Chip
            key={tag._id}
            label={tag.name}
            onClick={() => onTagClick(tag._id)}
            sx={{
              backgroundColor: isSelected ? '#3f51b5' : '#f1f1f1',
              color: isSelected ? '#fff' : '#333',
              borderRadius: '16px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: isSelected ? '#303f9f' : '#e0e0e0',
              },
            }}
          />
        ) : null;
      })}
    </Box>
  );
};

export default ActuView;